import type {
  WorkspaceEditorState,
  WorkspaceSuiteTest,
  WorkspaceTestLineVerdict,
} from "../../types";

// Kept out of `WorkspaceMonacoEditor` so the panel can read it without pulling
// the lazily loaded Monaco chunk into its own.

/**
 * Test-file behaviour layered on the editor: a pass/fail mark in the gutter
 * beside each test the last run judged, and — on the generated suite — an
 * "Edit as my test" CodeLens above each test.
 */
export interface WorkspaceSuiteOverlay {
  verdicts: WorkspaceTestLineVerdict[];
  /** The generated suite's adoption lenses; `null` on the user's own file. */
  adoption: {
    tests: WorkspaceSuiteTest[];
    adoptingTestId: string | null;
    onAdopt: (testId: string) => void;
  } | null;
  /** Shown when the author tries to type into a read-only suite. */
  readOnlyMessage: string | null;
}

export const TEST_VERDICT_PASSED_CLASS = "ig-test-verdict-passed";
export const TEST_VERDICT_FAILED_CLASS = "ig-test-verdict-failed";
export const TEST_VERDICT_SKIPPED_CLASS = "ig-test-verdict-skipped";

/** Monaco's hint when the author types into the generated suite. */
export const GENERATED_SUITE_READ_ONLY_MESSAGE =
  "Edit a test to make it yours: choose “Edit as my test” above it.";

/** The editor-side behaviour of the open test file, or `null` on a rule file. */
export const suiteOverlayFor = (
  editor: WorkspaceEditorState,
  isHeld: boolean
): WorkspaceSuiteOverlay | null => {
  const testFiles = editor.testFiles;
  if (testFiles === null || testFiles.active === null) {
    return null;
  }
  if (testFiles.active === "user") {
    return {
      verdicts: testFiles.user.verdicts,
      adoption: null,
      readOnlyMessage: null,
    };
  }
  const { generated } = testFiles;
  return {
    verdicts: generated.verdicts,
    // Held while a run or an AI author owns the draft: adopting writes the
    // draft too, so it waits exactly as typing does.
    adoption: isHeld
      ? null
      : {
          tests: generated.tests,
          adoptingTestId: generated.adoptingTestId,
          onAdopt: generated.adoptTest,
        },
    readOnlyMessage: GENERATED_SUITE_READ_ONLY_MESSAGE,
  };
};
